/**
 * NotesService
 *
 * This service handles all note-related operations in the application.
 * It provides methods for managing notes, including creation, retrieval,
 * updating, and status management. The service supports operations for both
 * individual notes and system-wide note management
 *
 * @class
 */
import { pathNotesMatterId, pathNotesMatterIdNoteId } from '@frontend/__settings__/septSoftwarePaths';
import HttpService from './HttpService';
import { INote } from '@frontend/Models/Notes/NotesModels';
import { AddNoteRequest, AddNoteResponse } from '@frontend/Models/Notes/NotesRequestResponseModels';

const http = new HttpService();

class NotesService {
  /**
   * Retrieves notes associated with a specific matter ID.
   *
   * @param matterId The ID of the matter to retrieve notes for.
   * @returns A promise that resolves to the notes for the specified matter.
   */
  static async getNotesByMatterId(
    matterId: string
  ): Promise<INote[]> {
    return http.get<INote[]>(pathNotesMatterId(matterId));
  }

  /**
   * Add a matter to a new note.
   * @param matterId the matter ID to associate the note with
   * @param noteData the note data including content and author
   * @returns A promise that resolves to the response after adding the note.
   */
  static async addMatterNote(
    matterId: string,
    noteData: AddNoteRequest,
  ): Promise<AddNoteResponse> {
    return http.post<AddNoteResponse>(pathNotesMatterId(matterId), noteData);
  }

  /**
   * Update an existing note.
   * @param matterId the matter ID the note belongs to
   * @param noteId the ID of the note to update
   * @param noteData the updated note data
   * @returns A promise that resolves to the response after updating the note.
   */
  static async updateMatterNote(
    matterId: string,
    noteId: number,
    noteData: { note_text: string; title?: string },
  ): Promise<{ message: string }> {
    return http.put<{ message: string }>(pathNotesMatterIdNoteId(matterId, noteId), noteData);
  }

  /**
   * Delete a note.
   * @param matterId the matter ID the note belongs to
   * @param noteId the ID of the note to delete
   * @returns A promise that resolves to the response after deleting the note.
   */
  static async deleteMatterNote(
    matterId: string,
    noteId: number,
  ): Promise<{ message: string }> {
    return http.delete<{ message: string }>(pathNotesMatterIdNoteId(matterId, noteId));
  }

}

export default NotesService;
